import { DialogPrompt } from '@app/components/dialog-prompt/dialog-prompt';
import { CaretDown } from '@app/components/icons/caret-down';
import { ShareWithArrowIcon } from '@app/components/icons/share-with-arrow';
import { Tooltip } from '@app/components/tooltip';
import { useFlag } from '@app/feature-flags/use-flag';
import { useAppSelector } from '@app/store/store';
import { FLAG_POST_EVENT_ORG_RECORDINGS_UPLOAD } from '@app/utilities/flags';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, { useRef } from 'react';
import FocusLock from 'react-focus-lock';

import { selectSelectedClip } from '../../selector';
import { AnchoredPanel } from './anchored-panel';
import { ShareMenu as LegacyShareMenu } from './legacy-share-menu';
import { PublishToEvents } from './publish-to-events';
import { type ShareActionsHandle, ShareActions } from './share-actions';
import { STEP } from './types';
import { useShareMenuFlow } from './use-share-menu-flow';

export const ShareMenu = ({ label }: { label?: string }) => {
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const isOrgRecordingsUploadEnabled = useFlag(
    FLAG_POST_EVENT_ORG_RECORDINGS_UPLOAD,
  );
  const selectedClip = useAppSelector(selectSelectedClip);
  const isPublishShareDisabled = selectedClip != null;

  const {
    isOpen,
    step,
    publishTriggerRef,
    isConfirmationShown,
    toggleMenu,
    goToPublishStep,
    requestBack,
    requestClose,
    keepEditing,
    discardChanges,
  } = useShareMenuFlow();

  if (!isOrgRecordingsUploadEnabled) {
    return <LegacyShareMenu />;
  }

  return (
    <Tooltip
      tooltip={t('content-recording.publish-share-disabled-while-editing')}
      shouldShow={isPublishShareDisabled}
      childClassName="inline-block"
    >
      <div
        className={cx(
          'relative inline-block',
          isPublishShareDisabled && 'cursor-not-allowed',
        )}
      >
        <div className={cx('relative', isPublishShareDisabled && 'opacity-60')}>
          <button
            type="button"
            ref={triggerRef}
            disabled={isPublishShareDisabled}
            className={cx(
              'flex items-center',
              'border border-solid border-gray-600 rounded-10',
              'hover:bg-spring-neutral-b0/10 transition-colors',
              'outline-none ui-focus-visible:ring-2 ui-focus-visible:ring-blue-500 ui-focus-visible:ring-offset-2 ui-focus-visible:ring-offset-spring-neutral-base',
            )}
            onClick={toggleMenu}
            data-testid="share-menu-button"
          >
            <div className="flex items-center gap-2 pl-4 pr-2 py-2 border-r border-gray-600 text-sm font-medium text-spring-neutral-b0">
              {label ?? t('content-recording.publish-and-share')}
              <ShareWithArrowIcon className="w-5 h-5" />
            </div>

            <CaretDown
              className={cx('w-5 h-5 m-2', { 'rotate-180': isOpen })}
            />
          </button>

          <AnchoredPanel
            anchorRef={triggerRef}
            open={isOpen}
            onRequestClose={requestClose}
          >
            <FocusLock returnFocus disabled={isConfirmationShown}>
              <ShareMenuContent
                onClose={requestClose}
                onBack={requestBack}
                onPublishToEvent={goToPublishStep}
                step={step}
                publishTriggerRef={publishTriggerRef}
              />
            </FocusLock>
          </AnchoredPanel>

          <DialogPrompt
            isShown={isConfirmationShown}
            isDarkTheme={true}
            onClose={keepEditing}
            onConfirm={discardChanges}
            title={t('content-recording.discard-publish-changes.title')}
            description={t(
              'content-recording.discard-publish-changes.description',
            )}
            confirmText={t('content-recording.discard-publish-changes.confirm')}
            cancelText={t('content-recording.discard-publish-changes.cancel')}
          />
        </div>
      </div>
    </Tooltip>
  );
};

const ShareMenuContent = ({
  onClose,
  onBack,
  onPublishToEvent,
  step,
  publishTriggerRef,
}: {
  onClose: () => void;
  onBack: () => void;
  onPublishToEvent: () => void;
  step: ReturnType<typeof useShareMenuFlow>['step'];
  publishTriggerRef: React.RefObject<ShareActionsHandle>;
}) => {
  return (
    <div tabIndex={-1}>
      {step === STEP.SHARE_ACTIONS && (
        <ShareActions
          ref={publishTriggerRef}
          onClose={onClose}
          onPublishToEvent={onPublishToEvent}
        />
      )}

      {step === STEP.PUBLISH_TO_EVENT && (
        <PublishToEvents onClose={onClose} onBack={onBack} />
      )}
    </div>
  );
};
